import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone, ChevronDown, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const locations = [
    { name: t('locations.frankfurtSchwanheim'), slug: 'frankfurt-schwanheim' },
    { name: t('locations.frankfurtNiederrad'), slug: 'frankfurt-niederrad' },
    { name: t('locations.offenbach'), slug: 'offenbach' },
    { name: t('locations.badHomburg'), slug: 'bad-homburg' },
  ];

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.pricing'), path: '/fuehrerschein' },
    { name: t('nav.theoryClass'), path: '/theorie-kalender' },
    { name: t('nav.contact'), path: '/kontakt' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 w-full bg-white shadow-md"
      data-testid="navbar"
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - ganz links (Dunkles Logo für hellen Hintergrund) */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_303cab3b-acc1-4381-b3cd-1e06f864619c/artifacts/0mppqdlm_Logo-Roessner-Dunkel.png"
              alt="Fahrschule Rößner Logo"
              className="h-10 md:h-12 lg:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation - in der Mitte */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <div className="flex items-center gap-1">
              {navLinks.slice(0, 1).map((link) => (
                <Link 
                  key={link.path}
                  to={link.path} 
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-slate-700 hover:text-blue-600 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.name}
                </Link>
              ))}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                      location.pathname.includes('/standorte')
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-slate-700 hover:text-blue-600 hover:bg-slate-100'
                    }`}
                  data-testid="nav-standorte-dropdown"
                >
                  {t('nav.locations')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64 p-2">
                {locations.map((loc) => (
                  <DropdownMenuItem key={loc.slug} asChild>
                    <Link 
                      to={`/standorte/${loc.slug}`}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer hover:bg-slate-100"
                      data-testid={`nav-location-${loc.slug}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium">{loc.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {navLinks.slice(1).map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive(link.path) || (link.path === '/blog' && location.pathname.startsWith('/blog'))
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-slate-700 hover:text-blue-600 hover:bg-slate-100'
                }`}
                data-testid={`nav-${link.path.replace('/', '')}`}
              >
                {link.name}
              </Link>
            ))}
            </div>
          </div>

          {/* CTA & Phone & Language - ganz rechts */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <LanguageSwitcher />
            <a 
              href="tel:+496920326059" 
              className="flex items-center gap-2 font-medium text-slate-700 hover:text-blue-600 transition-colors"
              data-testid="nav-phone"
            >
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </div>
              <span className="hidden xl:inline">069 20 32 60 59</span>
            </a>
            <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
              <Button 
                type="submit"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 h-11 font-semibold shadow-lg shadow-blue-500/20 btn-modern"
                data-testid="nav-cta-button"
              >
                {t('nav.callNow')}
              </Button>
            </form>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center transition-colors bg-slate-100 text-slate-700"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-x-0 top-20 bg-white shadow-2xl rounded-b-3xl transition-all duration-500 ease-out ${
            isOpen 
              ? 'opacity-100 translate-y-0 pointer-events-auto' 
              : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          data-testid="mobile-menu"
        >
          <div className="p-6 space-y-2">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            
            <div className="pt-4 pb-2">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 mb-2">{t('nav.locations')}</div>
              {locations.map((loc) => (
                <Link 
                  key={loc.slug}
                  to={`/standorte/${loc.slug}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  <MapPin className="w-4 h-4 text-blue-600" />
                  {loc.name}
                </Link>
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="px-4 py-2 mb-2">
                <LanguageSwitcher />
              </div>
              <a 
                href="tel:+496920326059" 
                className="flex items-center gap-3 px-4 py-3 text-slate-700"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">{t('contact.info.phone')}</div>
                  <div className="font-semibold">069 20 32 60 59</div>
                </div>
              </a>
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D" className="block mt-4" onClick={() => setIsOpen(false)}>
                <Button type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold">
                  {t('nav.callNow')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
